import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import StatesPicker from "../components/StatesPicker";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    email: "", password: "", name: "", phone: "", buffer_tier: 75, states: [],
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.states.length === 0) {
      setErr("Select at least one state you are licensed in.");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      await register({ ...form, buffer_tier: Number(form.buffer_tier) });
      toast.success("Account created");
      nav("/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-zinc-950 text-white">
        <div className="flex items-center gap-2.5">
          <img src="/favicon.svg" alt="" aria-hidden="true" className="w-7 h-7" />
          <div className="font-heading font-black text-2xl tracking-tight">LeadBridge</div>
        </div>
        <div>
          <div className="font-heading font-black text-5xl tracking-tight leading-none">
            Get on <br />the phone.
          </div>
          <p className="text-zinc-400 mt-6 max-w-xs text-sm">
            Two minutes to register. Approval is fast. Your wallet funds in 30 seconds via secure checkout.
          </p>
        </div>
        <div className="text-xs font-mono tracking-widest uppercase text-zinc-500">
          Approval required before going live
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-xl">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 mb-2">New agent</div>
          <h1 className="font-heading font-black text-4xl tracking-tight leading-none mb-8">Register</h1>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-600">Full name</Label>
              <Input
                value={form.name}
                required
                onChange={(e) => update("name", e.target.value)}
                className="btn-sharp mt-1 h-11"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-600">Email</Label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="btn-sharp mt-1 h-11"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-600">Phone (cell) — E.164</Label>
              <Input
                placeholder="+15551234567"
                value={form.phone}
                required
                onChange={(e) => update("phone", e.target.value)}
                className="btn-sharp mt-1 h-11 font-mono"
                data-testid="register-phone-input"
              />
              <div className="text-[11px] text-zinc-500 mt-1">Calls will be routed to this number.</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-600">Password</Label>
              <Input
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => update("password", e.target.value)}
                className="btn-sharp mt-1 h-11"
                data-testid="register-password-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-600">Buffer tier</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {[
                  { v: 75, p: 40 },
                  { v: 115, p: 60 },
                ].map((t) => (
                  <button
                    type="button"
                    key={t.v}
                    onClick={() => update("buffer_tier", t.v)}
                    className={`border p-3 text-left btn-sharp transition ${
                      Number(form.buffer_tier) === t.v
                        ? "border-black bg-black text-white"
                        : "border-zinc-300 bg-white hover:border-black"
                    }`}
                    data-testid={`register-tier-${t.v}-button`}
                  >
                    <div className="font-mono font-bold text-2xl">${t.p}</div>
                    <div className="text-[10px] uppercase tracking-widest mt-1">{t.v} s buffer</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-600">Licensed states</Label>
              <div className="mt-2">
                <StatesPicker
                  value={form.states}
                  onChange={(v) => update("states", v)}
                  testIdPrefix="register-states"
                />
              </div>
              <div className="text-[11px] text-zinc-500 mt-2">
                Calls only route to you if the caller's state matches.
              </div>
            </div>
            {err && (
              <div className="text-sm text-[#E63946] font-medium" data-testid="register-error">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              className="w-full btn-sharp bg-[#0055FF] hover:bg-[#0044CC] text-white h-12 font-bold tracking-wider uppercase text-xs"
              data-testid="register-submit-button"
            >
              {busy ? "Creating…" : "Create my agent account"}
            </Button>
          </div>

          <div className="mt-6 text-sm text-zinc-600">
            Already have an account?{" "}
            <Link to="/login" className="font-bold text-black underline" data-testid="register-login-link">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
